import React, { useEffect, useState, useMemo } from 'react';
import { useSubscription, getUserTier } from '../contexts/SubscriptionContext';
import { STRIPE_ENABLED } from '../config/stripe';
import { isPaymentsEnabled } from '../config/featureFlags';
import { PaymentsServiceApi } from '../services/PaymentsServiceApi';

/**
 * BillingStatusPage
 * Handles return from Stripe Checkout (success / canceled) and gives user
 * clear next steps. On success it polls subscription until active or timeout.
 * URL query params:
 *   ?success=1  - checkout success redirect
 *   ?canceled=1 - checkout canceled redirect
 */
export default function BillingStatusPage() {
  const params = new URLSearchParams(window.location.search);
  const success = params.has('success');
  const canceled = params.has('canceled');
  const { subscription, startActivationPolling, pollTimeout, initialized, loading, refresh } = useSubscription();
  const [error] = useState(null);
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [plans, setPlans] = useState([]);
  const enabled = isPaymentsEnabled() && STRIPE_ENABLED;
  const tier = getUserTier(subscription);

  // Load plans to get human-readable names
  useEffect(() => {
    if (!enabled) return;
    PaymentsServiceApi.listPlans()
      .then(serverPlans => {
        if (Array.isArray(serverPlans) && serverPlans.length) {
          setPlans(serverPlans);
        }
      })
      .catch(() => setPlans([]));
  }, [enabled]);

  // Find matching plan for display
  const matchedPlan = useMemo(() => {
    if (!subscription || !plans.length) return null;
    const subPriceId = subscription.priceId || subscription.items?.[0]?.price?.id || subscription.items?.[0]?.price;
    return plans.find(p => 
      (p.priceId && subPriceId && p.priceId === subPriceId) || 
      (p.alias === subscription.planAlias)
    );
  }, [subscription, plans]);

  const planDisplayName = useMemo(() => {
    if (matchedPlan?.label) return matchedPlan.label;
    if (subscription?.planAlias && subscription.planAlias !== subscription.priceId) {
      return subscription.planAlias.charAt(0).toUpperCase() + subscription.planAlias.slice(1);
    }
    if (tier && tier !== 'free') {
      return tier.charAt(0).toUpperCase() + tier.slice(1);
    }
    return subscription?.priceId || subscription?.planAlias || '—';
  }, [matchedPlan, subscription, tier]);

  const planAmount = useMemo(() => {
    if (matchedPlan?.amount && matchedPlan?.currency) {
      const amount = matchedPlan.amount / 100;
      return amount.toLocaleString(undefined, { style: 'currency', currency: matchedPlan.currency });
    }
    return null;
  }, [matchedPlan]);

  useEffect(() => {
    if (!initialized || loading || hasRefreshed) return; // wait for subscription context ready, prevent repeated refreshes
    if (success && enabled) {
      startActivationPolling();
      setHasRefreshed(true);
    } else if (!success && !subscription) {
      // Only refresh once if no subscription data loaded yet
      refresh();
      setHasRefreshed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, enabled, initialized, loading, hasRefreshed]);

  // Helper to format dates in a friendly way
  const formatDateFriendly = (dateValue) => {
    if (!dateValue) return '—';
    try {
      const date = typeof dateValue === 'number' ? new Date(dateValue * 1000) : new Date(dateValue);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return '—';
    }
  };

  return (
    <div style={{maxWidth:680,margin:'40px auto',padding:'0 20px'}}>
      {/* Success Banner with Icon */}
      {success && enabled && (
        <div style={{
          background:'var(--color-status-success, #10b981)',
          color:'var(--text-inverse, #ffffff)',
          padding:'20px 24px',
          borderRadius:'var(--radius-card, 12px)',
          marginBottom:'24px',
          display:'flex',
          alignItems:'center',
          gap:'16px',
          boxShadow:'var(--shadow-brand, 0 4px 12px rgba(16,185,129,0.3))'
        }}>
          <div style={{
            width:'48px',
            height:'48px',
            borderRadius:'50%',
            background:'rgba(255,255,255,0.2)',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            fontSize:'24px',
            flexShrink:0
          }}>✓</div>
          <div>
            <div style={{fontSize:'1.25rem',fontWeight:'600',marginBottom:'4px'}}>
              Payment Successful!
            </div>
            <div style={{fontSize:'0.95rem',opacity:0.95}}>
              Your subscription is now active. Welcome aboard!
            </div>
          </div>
        </div>
      )}

      {/* Other Banners */}
      {!enabled && (
        <div style={bannerStyle('warn')}>Payments feature disabled.</div>
      )}
      {canceled && (
        <div style={bannerStyle('warn')}>
          Checkout canceled. You were not charged.
        </div>
      )}
      {pollTimeout && !subscription && (
        <div style={bannerStyle('warn')}>Still finalizing subscription — refresh later or contact support.</div>
      )}
      {error && <div style={bannerStyle('warn')}>{error}</div>}

      {/* Main Subscription Card */}
      <section style={{
        background:'var(--card-bg, #ffffff)',
        border:'1px solid var(--border-default, #e2e8f0)',
        borderRadius:'var(--radius-card, 16px)',
        padding:'32px',
        marginBottom:'24px',
        boxShadow:'var(--shadow-card, 0 1px 3px rgba(0,0,0,0.05))'
      }}>
        {!subscription && !success && (
          <div style={{textAlign:'center',color:'var(--text-muted, #64748b)',fontSize:'1rem'}}>
            No active subscription.
          </div>
        )}
        {!subscription && success && (
          <div style={{textAlign:'center',color:'var(--text-muted, #64748b)',fontSize:'1rem'}}>
            <div style={{fontSize:'1.5rem',marginBottom:'12px'}}>⏳</div>
            Activating your subscription…
          </div>
        )}
        {subscription && (
          <div>
            {/* Plan Header */}
            <div style={{marginBottom:'24px'}}>
              <div style={{fontSize:'0.85rem',textTransform:'uppercase',letterSpacing:'0.05em',color:'var(--text-muted, #64748b)',marginBottom:'8px'}}>
                Your Plan
              </div>
              <div style={{fontSize:'2rem',fontWeight:'700',color:'var(--text-primary, #1e293b)',marginBottom:'4px'}}>
                {planDisplayName}
              </div>
              {planAmount && (
                <div style={{fontSize:'1.1rem',color:'var(--text-secondary, #64748b)'}}>
                  {planAmount} / {matchedPlan?.interval || 'month'}
                </div>
              )}
            </div>

            {/* Status Badge */}
            <div style={{marginBottom:'24px'}}>
              <span style={{
                display:'inline-block',
                padding:'6px 14px',
                borderRadius:'20px',
                fontSize:'0.85rem',
                fontWeight:'600',
                background: subscription.status === 'active' ? 'var(--color-surface-success, #d1fae5)' : 'var(--intent-warning-bg, #fef3c7)',
                color: subscription.status === 'active' ? 'var(--color-text-success, #065f46)' : 'var(--intent-warning-text, #92400e)'
              }}>
                {subscription.status === 'active' ? '● Active' : `● ${subscription.status}`}
              </span>
            </div>

            {/* Subscription Details */}
            <div style={{
              background:'var(--elevated-bg, #ffffff)',
              border:'1px solid var(--border-subtle, #e2e8f0)',
              borderRadius:'var(--radius-button, 12px)',
              padding:'20px',
              marginBottom:'24px'
            }}>
              <div style={{display:'grid',gap:'14px'}}>
                {subscription.currentPeriodEnd && (
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{color:'var(--text-muted, #64748b)',fontSize:'0.9rem'}}>Renewal Date</span>
                    <span style={{fontWeight:'600',color:'var(--text-primary, #1e293b)',fontSize:'0.95rem'}}>
                      {formatDateFriendly(subscription.currentPeriodEnd)}
                    </span>
                  </div>
                )}
                {subscription.cancelAtPeriodEnd && (
                  <div style={{
                    padding:'12px',
                    background:'var(--intent-danger-bg, #fef2f2)',
                    border:'1px solid var(--intent-danger-border, #fecaca)',
                    borderRadius:'var(--radius-md, 8px)',
                    color:'var(--intent-danger-text, #991b1b)',
                    fontSize:'0.9rem'
                  }}>
                    ⚠️ Cancellation scheduled for end of billing period
                  </div>
                )}
              </div>
            </div>

            {/* What's Included */}
            {matchedPlan && (
              <div style={{marginBottom:'24px'}}>
                <div style={{fontSize:'1.1rem',fontWeight:'600',color:'var(--text-primary, #1e293b)',marginBottom:'12px'}}>
                  What's Included
                </div>
                <div style={{display:'grid',gap:'8px'}}>
                  {['Unlimited course access', 'AI-powered learning assistant', 'Priority support', 'Progress tracking'].map((feature, i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',fontSize:'0.9rem',color:'var(--text-secondary, #475569)'}}>
                      <span style={{color:'var(--color-status-success, #10b981)',fontSize:'1.1rem'}}>✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
              <button 
                className="btn-primary" 
                onClick={()=>window.location.href='/subscription'}
                style={{
                  display:'flex',
                  alignItems:'center',
                  gap:'8px',
                  fontSize:'0.95rem',
                  padding:'12px 24px'
                }}
              >
                <span>⚙️</span> Manage Subscription
              </button>
              <button 
                className="btn-outline" 
                onClick={()=>window.location.href='/dashboard'}
                style={{
                  display:'flex',
                  alignItems:'center',
                  gap:'8px',
                  fontSize:'0.95rem',
                  padding:'12px 24px'
                }}
              >
                <span>🏠</span> Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Next Steps */}
      <section style={{
        background:'var(--card-bg, #ffffff)',
        border:'1px solid var(--border-default, #e2e8f0)',
        borderRadius:'var(--radius-card, 12px)',
        padding:'24px',
        fontSize:'0.9rem'
      }}>
        <h2 style={{fontSize:'1.1rem',fontWeight:'600',color:'var(--text-primary, #1e293b)',marginBottom:'16px'}}>
          Need Help?
        </h2>
        <div style={{display:'grid',gap:'12px',color:'var(--text-secondary, #475569)',lineHeight:'1.6'}}>
          <div style={{display:'flex',gap:'10px'}}>
            <span style={{color:'var(--text-muted, #64748b)'}}>•</span>
            <span>Manage payment methods or cancel anytime from Subscription Management</span>
          </div>
          <div style={{display:'flex',gap:'10px'}}>
            <span style={{color:'var(--text-muted, #64748b)'}}>•</span>
            <span>If your subscription status doesn't update, try refreshing the page</span>
          </div>
          <div style={{display:'flex',gap:'10px'}}>
            <span style={{color:'var(--text-muted, #64748b)'}}>•</span>
            <span>Have billing questions? Contact our support team anytime</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function bannerStyle(type){
  const base = {margin:'0 0 18px',padding:'10px 14px',borderRadius:'10px',fontSize:'.75rem',fontWeight:600,letterSpacing:'.3px'};
  if (type==='success') return {...base,background:'#ecfdf5',color:'#065f46',border:'1px solid #34d399'};
  if (type==='warn') return {...base,background:'#fff7ed',color:'#9a3412',border:'1px solid #fb923c'};
  return base;
}
